import { ChevronRight, GripVertical, Plus, Search } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { OwnerCreateDialog } from "@/components/owners/owner-create-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getOwnerInitials, type OrgOwner, OWNER_DRAG_MIME } from "./org-chart-utils";

interface OwnerPanelProps {
  owners: OrgOwner[];
  collapsed: boolean;
  onToggle: () => void;
  onOwnerCreated?: () => void;
}

export function OwnerPanel({ owners, collapsed, onToggle, onOwnerCreated }: OwnerPanelProps) {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return owners;
    const q = search.toLowerCase();
    return owners.filter(
      (o) =>
        o.firstName.toLowerCase().includes(q) ||
        o.lastName.toLowerCase().includes(q) ||
        (o.role?.toLowerCase().includes(q) ?? false),
    );
  }, [owners, search]);

  const handleDragStart = (e: React.DragEvent, owner: OrgOwner) => {
    e.dataTransfer.setData(OWNER_DRAG_MIME, owner.id);
    e.dataTransfer.effectAllowed = "move";
  };

  if (collapsed) {
    return (
      <div className="border-l border-border bg-card flex flex-col items-center py-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle}>
          <ChevronRight className="h-4 w-4 rotate-180" />
        </Button>
        <div className="mt-2 -rotate-90 whitespace-nowrap text-xs text-muted-foreground origin-center translate-y-12">
          {owners.length} available
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 border-l border-border bg-card flex flex-col">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Available Owners</h3>
          <p className="text-xs text-muted-foreground">{owners.length} unassigned</p>
        </div>
        <div className="flex items-center gap-1">
          {onOwnerCreated && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCreateOpen(true)}
              title="Create owner"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search owners..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filtered.map((owner) => {
          const initials = getOwnerInitials(owner.firstName, owner.lastName);
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: drag source for drag-and-drop
            <div
              key={owner.id}
              draggable
              onDragStart={(e) => handleDragStart(e, owner)}
              className={cn(
                "flex items-center gap-2.5 p-2.5 rounded-md border border-border bg-background",
                "cursor-grab active:cursor-grabbing hover:border-brand-gold/40 hover:shadow-sm transition-colors",
              )}
            >
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold text-xs font-semibold">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {owner.firstName} {owner.lastName}
                </p>
                {owner.role && (
                  <p className="text-xs text-muted-foreground truncate">{owner.role}</p>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {search ? "No matching owners" : "All owners are assigned"}
          </p>
        )}
      </div>

      {onOwnerCreated && (
        <OwnerCreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={onOwnerCreated}
        />
      )}
    </div>
  );
}
